import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './connectionToast.css';

/**
 * YouTube-style connection toast (top centre, every page).
 * Lost: the browser's `offline` event, or a panel request that got no answer at all → one /health
 * check confirms it (a single failed request isn't enough) → "You're offline" (no network) or
 * "Can't reach the panel" (network up, panel down, e.g. a deploy), retrying 2 → 4 → 8 → 15 s.
 * Back: "Back online" for 3 s, and `flux:reconnected` is dispatched on window so pages can refetch.
 */
const BACKOFF = [2, 4, 8, 15];
const BACK_MS = 3000;
const GATEWAY_ERRORS = [502, 503, 504, 520, 521, 522, 523, 524];

async function panelReachable() {
  try {
    await axios.get(`/health?_=${Date.now()}`, { timeout: 4000, __connCheck: true });
    return true;
  } catch (_) { return false; }
}

export default function ConnectionToast() {
  const { t } = useTranslation();
  const [state, setState] = useState('online'); // online | lost | checking | back
  const [noNetwork, setNoNetwork] = useState(false);
  const [secs, setSecs] = useState(0);
  const lostRef = useRef(false);
  const attemptRef = useRef(0);
  const timerRef = useRef(null);
  const tickRef = useRef(null);
  const backRef = useRef(null);
  const checkingRef = useRef(false);

  const clearTimers = () => {
    clearTimeout(timerRef.current);
    clearInterval(tickRef.current);
  };

  const check = useCallback(async () => {
    if (checkingRef.current) return;
    checkingRef.current = true;
    clearTimers();
    if (lostRef.current) setState('checking');
    const ok = typeof navigator !== 'undefined' && navigator.onLine === false ? false : await panelReachable();
    checkingRef.current = false;
    if (ok) {
      if (lostRef.current) {
        lostRef.current = false;
        attemptRef.current = 0;
        setState('back');
        clearTimeout(backRef.current);
        backRef.current = setTimeout(() => setState('online'), BACK_MS);
        window.dispatchEvent(new Event('flux:reconnected'));
      }
      return;
    }
    // still (or newly) disconnected: schedule the next try
    lostRef.current = true;
    clearTimeout(backRef.current);
    setNoNetwork(typeof navigator !== 'undefined' && navigator.onLine === false);
    const wait = BACKOFF[Math.min(attemptRef.current, BACKOFF.length - 1)];
    attemptRef.current += 1;
    setSecs(wait);
    setState('lost');
    tickRef.current = setInterval(() => setSecs(s => (s > 1 ? s - 1 : s)), 1000);
    timerRef.current = setTimeout(check, wait * 1000);
  }, []);

  useEffect(() => {
    const onOffline = () => check();
    const onOnline = () => { attemptRef.current = 0; check(); };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    // A panel request with no response at all (not cancelled), or a gateway error while the panel is
    // down (Caddy / Cloudflare answer 502-504 / 52x during a deploy) → confirm with /health
    const id = axios.interceptors.response.use(undefined, (err) => {
      const cfg = err && err.config;
      const status = err?.response?.status;
      const noAnswer = !err?.response && !axios.isCancel(err);
      const gateway = GATEWAY_ERRORS.includes(status);
      if ((noAnswer || gateway) && !(cfg && cfg.__connCheck) && !lostRef.current) check();
      return Promise.reject(err);
    });
    if (typeof navigator !== 'undefined' && navigator.onLine === false) check();
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      axios.interceptors.response.eject(id);
      clearTimers();
      clearTimeout(backRef.current);
    };
  }, [check]);

  if (state === 'online') return null;
  const back = state === 'back';
  const title = back
    ? t('connection.backOnline', 'Back online')
    : noNetwork ? t('connection.offline', "You're offline") : t('connection.unreachable', "Can't reach the panel");
  const sub = back ? null
    : state === 'checking' ? t('connection.reconnecting', 'Reconnecting…')
      : t('connection.reconnectingIn', 'Reconnecting in {{s}}s…', { s: secs });

  return createPortal(
    <div className={`conn-toast ${back ? 'back' : 'lost'}`} role="status" aria-live="polite">
      <span className="conn-ic" aria-hidden="true">
        {back ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5a10 10 0 0 1 14 0" /><path d="M8.5 16a5 5 0 0 1 7 0" /><path d="M12 19.5h.01" /><path d="M2 9a14.5 14.5 0 0 1 20 0" /></svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 2l20 20" /><path d="M8.5 16a5 5 0 0 1 7 0" /><path d="M12 19.5h.01" /><path d="M5 12.5a10 10 0 0 1 4.2-2.4" /><path d="M2 9a14.5 14.5 0 0 1 4.4-2.9" /><path d="M14.5 10.2A10 10 0 0 1 19 12.5" /><path d="M17.6 6.1A14.5 14.5 0 0 1 22 9" /></svg>
        )}
      </span>
      <span className="conn-text">
        <span className="conn-title">{title}</span>
        {sub && <span className="conn-sub">{state === 'checking' && <span className="conn-spin" aria-hidden="true" />}{sub}</span>}
      </span>
      {!back && (
        <button type="button" className="conn-retry" onClick={() => { attemptRef.current = 0; check(); }} disabled={state === 'checking'}>
          {t('connection.retry', 'Retry')}
        </button>
      )}
    </div>,
    document.body
  );
}
